import { restApiClient } from "@lobby/api-client";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import { getDocumentLocale, useAuth, type TTransactionHistory } from "../../shared";
import { Player } from "../player/player-model";

import {
  TRANSACTION_HISTORY_LIMIT,
  USE_REST_TRANSACTION_HISTORY,
  mapRestTransactionItem,
} from "./payment.transaction-history.helpers";

import type { ApiRestTypes } from "@lobby/api-client";
import type { InfiniteData } from "@tanstack/react-query";

type RestTransactionsPage = ApiRestTypes.DefinitionMap["PlayerTransactions.Data"];
type TransactionHistoryInfiniteData = InfiniteData<{
  result: { data: TTransactionHistory };
}>;

function getRestRequestLanguage(): string {
  try {
    const locale = localStorage.getItem("locale");
    if (locale) {
      return locale;
    }
  } catch {
    return getDocumentLocale();
  }

  return getDocumentLocale();
}

const paymentMethodsQueryOptions = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export const PaymentRest = {
  useMethodList(paymentType: "deposit" | "withdraw") {
    const { isAuth, isAuthInProgress } = useAuth();
    const { data: player } = Player.usePlayer();
    const language = getRestRequestLanguage();

    return useQuery({
      enabled: isAuth && !isAuthInProgress && Boolean(player?.id),
      queryKey: [
        "PaymentRest.getMethodsList",
        paymentType,
        language,
        player?.hallId,
        player?.currency,
      ],
      queryFn: () =>
        restApiClient.send({
          endpoint: "GET /api/v1/payment/methods",
          query: {
            direction: paymentType,
            language,
          },
        }),
      ...paymentMethodsQueryOptions,
    });
  },
  useCountriesList() {
    const language = getRestRequestLanguage();

    return useQuery({
      queryKey: ["PaymentRest.getCountries", language],
      queryFn: () =>
        restApiClient.send({
          endpoint: "GET /api/v1/countries",
          query: { language },
        }),
      ...paymentMethodsQueryOptions,
    });
  },
  useDeposit() {
    return useMutation({
      mutationKey: ["PaymentRest.deposit"],
      mutationFn: (body: ApiRestTypes.Input<"POST /api/v1/payment/deposit">["body"]) =>
        restApiClient.send({
          endpoint: "POST /api/v1/payment/deposit",
          body,
        }),
    });
  },
  useWithdraw() {
    return useMutation({
      mutationKey: ["PaymentRest.withdraw"],
      mutationFn: (body: ApiRestTypes.Input<"POST /api/v1/payment/withdraw">["body"]) =>
        restApiClient.send({
          endpoint: "POST /api/v1/payment/withdraw",
          body,
        }),
    });
  },
  useTransactionHistory(paymentType: "deposit" | "withdraw") {
    const { isAuth, isAuthInProgress } = useAuth();
    const { data: player } = Player.usePlayer();

    return useInfiniteQuery({
      enabled: USE_REST_TRANSACTION_HISTORY && isAuth && !isAuthInProgress && Boolean(player?.id),
      queryKey: ["PaymentRest.getTransactions", paymentType],
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        restApiClient.send({
          endpoint: "GET /api/v1/player/transactions",
          query: {
            type: paymentType,
            limit: TRANSACTION_HISTORY_LIMIT,
            offset: pageParam * TRANSACTION_HISTORY_LIMIT,
          },
        }),
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (!lastPage.items?.length || lastPage.items.length < TRANSACTION_HISTORY_LIMIT) {
          return undefined;
        }

        return lastPageParam + 1;
      },
      select: (data): TransactionHistoryInfiniteData => ({
        pages: data.pages.map((page: RestTransactionsPage, pageIndex) => ({
          result: {
            data: page.items.map((item, itemIndex) =>
              mapRestTransactionItem(item, pageIndex * TRANSACTION_HISTORY_LIMIT + itemIndex),
            ),
          },
        })),
        pageParams: data.pageParams,
      }),
      ...paymentMethodsQueryOptions,
    });
  },
};
